/**
 * Clipboard Dependencies
 * 
 * Functions for remapping dependencies after cloning.
 * Handles both v1 (flat array) and v2 (block-based) clipboard formats.
 */

import { getWaitSeconds } from '../../../shared/ConditionsDropdown/utils/conditionWait';

/**
 * Remap a single condition entry from clipboard index to new ID
 */
export function remapConditionEntry(cond, idMap, optionIdMap) {
  const taskIndex = cond.task;
  const newTaskId = idMap.get(taskIndex);
  if (!newTaskId) return null;

  let newBranch;
  if (cond.branch !== undefined) {
    newBranch = optionIdMap.get(`${taskIndex}-${cond.branch}`);
  }

  const waitSeconds = getWaitSeconds(cond);
  return {
    taskId: newTaskId,
    branch: newBranch || undefined,
    ...(waitSeconds ? { waitSeconds } : {}),
  };
}

/**
 * Remap a dependsOn entry (index or { task, branch, waitSeconds? }) to live IDs.
 * Plain string IDs stay string IDs unless a wait or branch is present.
 */
export function remapDependsOnEntry(dep, idMap, optionIdMap) {
  const cond = typeof dep === 'number' ? { task: dep } : dep;
  const entry = remapConditionEntry(cond, idMap, optionIdMap);
  if (!entry) return null;
  if (entry.branch || entry.waitSeconds) {
    return {
      taskId: entry.taskId,
      ...(entry.branch ? { branch: entry.branch } : {}),
      ...(entry.waitSeconds ? { waitSeconds: entry.waitSeconds } : {}),
    };
  }
  return entry.taskId;
}

/**
 * Remap dependencies from clipboard indices to new IDs
 */
export function remapDependencies(clonedTasks, idMap, optionIdMap) {
  clonedTasks.forEach(task => {
    if (task._unlockConditionsRaw) {
      const raw = task._unlockConditionsRaw;

      if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.version === 2) {
        // V2 block-based format
        const remappedBlocks = (raw.blocks || []).map(block => {
          const remappedConds = (block.conditions || [])
            .map(cond => remapConditionEntry(cond, idMap, optionIdMap))
            .filter(Boolean);
          return {
            conditions: remappedConds,
            innerOperator: block.innerOperator || 'AND',
            outerOperator: block.outerOperator || null,
          };
        }).filter(b => b.conditions.length > 0);

        if (remappedBlocks.length > 0) {
          remappedBlocks[0] = { ...remappedBlocks[0], outerOperator: null };
          task.unlockConditions = { version: 2, blocks: remappedBlocks };
        }
      } else if (Array.isArray(raw) && raw.length > 0) {
        // V1 flat array format
        task.unlockConditions = raw.map(cond => {
          const entry = remapConditionEntry(cond, idMap, optionIdMap);
          if (!entry) return null;
          return { ...entry, operator: cond.operator };
        }).filter(Boolean);
      }
    }
    
    if (task._dependsOnRaw && task._dependsOnRaw.length > 0) {
      task.dependsOn = task._dependsOnRaw
        .map(dep => remapDependsOnEntry(dep, idMap, optionIdMap))
        .filter(Boolean);
    }
    
    // Milestone host attachment: index -> new host task ID
    if (task._milestoneAnchorRaw) {
      const { task: hostIndex, side } = task._milestoneAnchorRaw;
      const newHostId = idMap.get(hostIndex);
      if (newHostId && (side === 'input' || side === 'output')) {
        task.milestoneAnchor = { hostTaskId: newHostId, side };
      }
    }

    if (task._noteAnchorRaw) {
      const { task: hostIndex } = task._noteAnchorRaw;
      const newHostId = idMap.get(hostIndex);
      if (newHostId) {
        task.noteAnchor = { hostTaskId: newHostId };
      }
    }
    
    delete task._unlockConditionsRaw;
    delete task._dependsOnRaw;
    delete task._clipboardIndex;
    delete task._milestoneAnchorRaw;
    delete task._noteAnchorRaw;
  });
}
